import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import { TextField } from '@flowwai-frontend/design-system'
import {
   validateLatitude,
   validateLongitude
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { MapCoordinates } from '../../types'
import * as Styles from './styles'

interface Props {
   value: MapCoordinates | null
   isReadOnly: boolean
   onChangeValue: (value: MapCoordinates) => void
   alignment?: 'HORIZONTAL' | 'VERTICAL'
}

const MapCoordinatesInputs = (props: Props): React.ReactElement => {
   const { value, isReadOnly, onChangeValue, alignment = 'HORIZONTAL' } = props

   const [latitude, setLatitude] = useState<string | null>(
      value?.lat.toString() ?? null
   )
   const [hasLatitudeError, setHasLatitudeError] = useState<boolean>(false)

   const [longitude, setLongitude] = useState<string | null>(
      value?.lng.toString() ?? null
   )
   const [hasLongitudeError, setHasLongitudeError] = useState<boolean>(false)

   const { mapsT: t } = useMapsUtilityContext()

   useEffect(() => {
      setLatitude(value?.lat.toString() ?? null)
      setLongitude(value?.lng.toString() ?? null)
      setHasLatitudeError(false)
      setHasLongitudeError(false)
   }, [`${value?.lat}:${value?.lng}`])

   const onValidateLatitude = (latitudeValue: number | null): boolean => {
      const isValidLatitude = latitudeValue
         ? validateLatitude(latitudeValue)
         : false

      if (!isValidLatitude) {
         setHasLatitudeError(true)
         return false
      }

      return true
   }

   const onValidateLongitude = (longitudeValue: number | null): boolean => {
      const isValidLongitude = longitudeValue
         ? validateLongitude(longitudeValue)
         : false

      if (!isValidLongitude) {
         setHasLongitudeError(true)
         return false
      }

      return true
   }

   const onBlurLatitude = () => {
      const latitudeValue = latitude ? Number(latitude) : null
      const isValidLatitude = onValidateLatitude(latitudeValue)

      if (!isValidLatitude) return

      setHasLatitudeError(false)

      const longitudeValue = longitude ? Number(longitude) : null

      const isValidLongitude = onValidateLongitude(longitudeValue)

      if (!isValidLongitude) return

      const areBothValuesPresent =
         latitudeValue !== null && longitudeValue !== null

      if (!areBothValuesPresent) return

      onChangeValue({
         lat: latitudeValue,
         lng: longitudeValue
      })
   }

   const onBlurLongitude = () => {
      const longitudeValue = longitude ? Number(longitude) : null
      const isValidLongitude = onValidateLongitude(longitudeValue)

      if (!isValidLongitude) return

      setHasLongitudeError(false)

      const latitudeValue = latitude ? Number(latitude) : null
      const isValidLatitude = onValidateLatitude(latitudeValue)

      if (!isValidLatitude) return

      const areBothValuesPresent =
         latitudeValue !== null && longitudeValue !== null
      if (!areBothValuesPresent) return

      onChangeValue({
         lat: latitudeValue,
         lng: longitudeValue
      })
   }

   const degrees = '\u00B0'

   const renderDegrees = (suffix: string): React.ReactElement => (
      <span
         className={cn('text-sm-medium', 'text-gray-400')}
      >{`${degrees}${suffix}`}</span>
   )

   const renderLatitude = (): React.ReactElement => (
      <div
         className={cn(
            Styles.coordinateInputContainerStyles,
            hasLatitudeError ? 'border-error-300' : Styles.inputBorderStyles
         )}
         title={latitude?.toString()}
      >
         <TextField
            value={latitude?.toString() ?? ''}
            onChange={setLatitude}
            onBlur={onBlurLatitude}
            size='ExtraSmall'
            placeholder={'Latitude'}
            rightElement={() => renderDegrees('N')}
            containerClassName='w-full'
            inputContainerClassName={cn(Styles.inputContainerStyles)}
            inputClassName={Styles.inputStyles}
            focusRingVariant={'None'}
            isDisabled={isReadOnly}
            onKeyDown={e => {
               if (e.key === 'Enter') {
                  onBlurLatitude()
               }
            }}
         />
      </div>
   )

   const renderLongitude = (): React.ReactElement => (
      <div
         className={cn(
            Styles.coordinateInputContainerStyles,
            hasLongitudeError ? 'border-error-300' : Styles.inputBorderStyles
         )}
         title={longitude?.toString()}
      >
         <TextField
            value={longitude?.toString() ?? ''}
            onChange={setLongitude}
            size='ExtraSmall'
            placeholder={'Longitude'}
            rightElement={() => renderDegrees('E')}
            containerClassName='w-full'
            inputContainerClassName={cn(Styles.inputContainerStyles)}
            inputClassName={Styles.inputStyles}
            focusRingVariant={'None'}
            onBlur={onBlurLongitude}
            isDisabled={isReadOnly}
            onKeyDown={e => {
               if (e.key === 'Enter') {
                  onBlurLongitude()
               }
            }}
         />
      </div>
   )

   const hasError = hasLatitudeError || hasLongitudeError

   return (
      <>
         <div
            className={cn('flex gap-2', {
               'flex-col': alignment === 'VERTICAL'
            })}
         >
            {renderLatitude()}
            {renderLongitude()}
         </div>
         {hasError ? (
            <p className={cn('text-xs-regular pl-8', 'text-error-500')}>
               {t('enterValidCoordinates')}
            </p>
         ) : null}
      </>
   )
}

export default observer(MapCoordinatesInputs)
